import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { TimecardRow } from '../models/timecardrow';
import { TimeCard } from '../models/timecard';
import { Project } from '../models/project';
import { ProjectService } from '../services/project/project.service';
import { TaskService } from '../services/task/task.service';
import { TimeCardService } from '../services/timecard/timecard.service';
import { WeekService } from '../services/week/week.service';
import { Week } from '../models/week';
import { Template } from '../models/template';
import { Task } from '../models/task';
import { TemplateService } from '../services/template/template.service';
import { Converters } from '../helpers/converters';
import { ReturnObject } from '../models/returnObject';
import { ApiTemplate } from '../models/apiTemplate';
import { ApiTimecard } from '../models/apiTimecard';
import { InternalMsgService } from '../services/internal.msg.service';
import { Subscription } from 'rxjs';
import { AccountService } from '../services/account.service';
import { AccountItem } from '../models/accountitem';
import { StatusMessagesService } from '../services/statusmessage.service';

@Component({
    selector: 'create-timecard-component',
    templateUrl: './createtimecard.component.html',
    styleUrls: ['./createtimecard.component.css']
})
export class CreateTimecardComponent implements OnInit, OnDestroy  {

    selectedWeek!: Week;
    weeks: Array<Week> = [];
    projectCtrl: FormControl = new FormControl();
    loggedAccount: AccountItem = new AccountItem();
    selectedTemplate: Template = new Template();
    msgSubscr!: Subscription;

    filteredProjects: Project[] = [];
    templates: Template[] = [];
    hourFieldNames: string[] = ['hourmonday','hourtueday','hourwedday','hourthuday','hourfriday','hoursatday','hoursunday'];
    allowedInputChars: string[] = ['0','1','2','3','4','5','6','7','8','9','.',',','-','backspace','delete','arrowleft','arrowright', 'tab','numpadsubtract','slash'];
    numbers: string[] = ['0','1','2','3','4','5','6','7','8','9'];
    deletedRows: Array<TimecardRow> = new Array<TimecardRow>();

    templateName = '';
    selectedDeleteTemplate: Template | null = null;
    templateAlreadyExists = false;
    deletedTemplateRows: Array<TimecardRow> = new Array<TimecardRow>();

    isDirty: boolean = false;
    showSubmitQuery = false;

    constructor(private projectService: ProjectService,
                private taskService: TaskService,
                private weekService: WeekService,
                private timecardService: TimeCardService,
                private templateService: TemplateService,
                private statusMessagesService: StatusMessagesService,
                private msgService: InternalMsgService,
                private accountService: AccountService) {}

  
    // -------------------------------------------------------------
    // Listeners
    // -------------------------------------------------------------
    @HostListener('window:beforeunload')
        canDeactivate(): Observable<boolean> | boolean {
            return !this.isDirty;
    }

    @HostListener( 'document:click', ['$event'] )
    handleClickEvent( event:MouseEvent ): boolean { 
        if (event.target instanceof HTMLInputElement) {
            let input: HTMLInputElement = event.target as HTMLInputElement;
            const isHourField = this.hourFieldNames.findIndex( x => x == input.name ) != -1;

            if (isHourField) {
                input.selectionStart = 0;
                input.selectionEnd = input.value.length;
                return false;
            }
        }
        return true;
    }


    @HostListener( 'document:keydown', ['$event'] )
    handleKeyboardEvent( event:KeyboardEvent ): boolean { 
        if (event.target instanceof HTMLInputElement) {
            let input: HTMLInputElement = event.target as HTMLInputElement;
            const isHourField = this.hourFieldNames.findIndex( x => x == input.name ) != -1;

            const isNumeric = this.numbers.findIndex( x => x == event.key.toLowerCase() ) != -1;
            const value: string = input.value;
            const isvalidKey = this.allowedInputChars.findIndex( x => x == event.key.toLowerCase() ) != -1;

            // if field other than hourXXXdat - allow any input
            if (!isHourField) return true;

            // if not an allowed key - abort
            if (!isvalidKey) return false;

            if (event.key == '-') 
            {
                if (input.value.includes('-')) return false;
                if (input.selectionStart && input.selectionStart > 0) return false;
                return true;
            }
            
            // only one of , or . is allowed - abort
            if ((value.includes('.') || value.includes(',')) && (event.key == ',' || event.key == '.') ) return false;

            // If input is comma then replace with period - abort
            if (event.key == ',') 
            {
                input.value = input.value + '.'; 
                return false;
            }

            if (input.selectionStart != input.selectionEnd) return true;

            // number cannot be bigger than 999 set to previous value - abort
            if ( isNumeric && Number.parseFloat(value + event.key) > 999) {
            input.value = input.value; ;
                return false;
            }
            
            // number cannot be smaller than -999 set to previous value - abort
            if ( isNumeric && Number.parseFloat(value + event.key) < -999) {
            input.value = input.value; ;
                return false;
            }
            
            // all checks passed
            return true;
        } 
        
        return true
    }
    
    // ----------------------------------------------------------------------------------
    // -- Life cycle hooks
    // ----------------------------------------------------------------------------------
    ngOnInit(): void {
        this.selectedWeek = new Week();

        this.msgService.onSelectedMenuSet( 1 );
        this.msgSubscr = this.msgService.loggedInAccountSet$.subscribe( 
            account => {
                this.loggedAccount = account;
                this.getWeeks();
                this.getTemplates();
        } );     
        this.accountService.fetchAccount();
    }

    ngOnDestroy(): void {
        this.msgSubscr.unsubscribe();
    }

    // ----------------------------------------------------------------------------------
    // -- HTTP 
    // ----------------------------------------------------------------------------------
    getWeeks(): void {
        this.msgService.ontoggleSpinnerSet(true);

        this.weekService.getReportableWeeks(this.loggedAccount.email)      
            .subscribe( { 
                next: (  data: ReturnObject<Week[]>  ) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;                    
                    }
    
                    this.weeks = Week.fromJSONArray(data.value);
                    this.sortWeeks();
                    this.filterWeeks();
                    this.createEmptyTimeCard();
                    this.setListWeekString();
                    this.selectedWeek = this.weeks[0];
                    this.weekSelected(this.selectedWeek);   
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    weekSelected(week:Week = new Week()): void {
        this.msgService.ontoggleSpinnerSet(true);

        this.deletedRows = new Array<TimecardRow>();
        this.selectedWeek = week;
        this.selectedWeek.timeCard = new TimeCard(null);
        this.selectedWeek.timeCard.timecardRows = [];
        this.projectService.getProjects(this.selectedWeek.weekNo)     
            .subscribe( { 
                next: (data: ReturnObject<Project[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.filteredProjects = data.value
                    this.getTimeCard();
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getTimeCard(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.timecardService.retrieveTimeCard(this.loggedAccount.email, this.selectedWeek.timeCardId, true)
            .subscribe( { 
                next: (timeCard: ReturnObject<ApiTimecard>) => {
                    if (timeCard.isFailure) {
                        this.statusMessagesService.showToasterError('Error', timeCard.failureMessage);
                        return;
                    }
                    if (timeCard.value != null) {
                        this.selectedWeek.timeCard = Week.fromApi(timeCard.value).timeCard;
                    }
                    this.setDirty(false);
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    selectProject(timecardRow: TimecardRow, project: Project, projectCtrl: FormControl): void {
        this.msgService.ontoggleSpinnerSet(true);

        timecardRow.tasksLoading = true;
        timecardRow.project = new Project();
        timecardRow.project.projNo = project.projNo;
        timecardRow.project.projName = project.projName;
        projectCtrl.reset();
        this.setDirty(true);

        this.taskService.getTasks(timecardRow.project.projNo, this.selectedWeek.weekNo)
            .subscribe( { 
                next: (data: ReturnObject<Array<Task>>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    if (data.value.length > 1) {
                        data.value = data.value.sort(function(a, b) {
                            return a.taskName == b.taskName ? 0 : +(a.taskName > b.taskName) || -1;
                        });
                        timecardRow.project.tasks = data.value;
                        timecardRow.tasksLoading = false;
                    } else if (data.value.length == 1) {
                        timecardRow.task = data.value[0];
                        timecardRow.project.tasks = data.value;
                        timecardRow.tasksLoading = false;
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    saveTimeCard(): void {
        this.msgService.ontoggleSpinnerSet(true);

        this.timecardService.saveTimeCard(this.loggedAccount.email, this.selectedWeek, 'saved')
            .subscribe( { 
                next: (data: ReturnObject<ApiTimecard>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.saveLastSavedTemplate();
                    this.selectedWeek.timeCardId = data.value.timeCardId;
                    this.weekSelected(this.selectedWeek);
                    const wks: Week | undefined = this.weeks.find( (x) => x.weekNo === this.selectedWeek.weekNo);
                    if (wks) {
                        wks.status = 'saved';
                        this.statusMessagesService.showToasterSuccess('Saved', 'Timecard saved successfully');
                        this.setDirty(false);
                    } else {
                        this.statusMessagesService.showToasterError('Error', 'Server did not report save status, please try again');
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    submitTimeCard(): void {
        this.msgService.ontoggleSpinnerSet(true);

        this.timecardService
            .saveTimeCard(this.loggedAccount.email, this.selectedWeek, 'submitted')
            .subscribe( { 
                next: (data: ReturnObject<ApiTimecard>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.saveLastSavedTemplate();
                    
                    this.getWeeks()
                    this.setDirty(false);
    
                    this.statusMessagesService.showToasterSuccess('Submitted', 'Timecard submitted successfully');
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    deleteTimecard() {
        this.msgService.ontoggleSpinnerSet(true);
        this.timecardService
            .deleteTimeCard(this.selectedWeek.timeCardId, this.loggedAccount.email)
            .subscribe( { 
                next: (data: ReturnObject<string>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.getWeeks();
                    this.setDirty(false);
    
                    this.statusMessagesService.showToasterSuccess('Deleted', data.value);
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    saveLastSavedTemplate(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.templateService
            .saveTemplate(this.loggedAccount.email, 'Last-Saved', this.selectedWeek.timeCard.timecardRows)
            .subscribe( { 
                next: (data: ReturnObject<ApiTemplate[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.templates = Converters.ApiTemplateToTemplate(data.value);
                    if ( !this.selectedTemplate ) {
                        this.selectedTemplate = this.templates[0];
                    } else {
                        const tmpl: Template | undefined =  this.templates.find( x => x.name === this.selectedTemplate.name);
                        this.selectedTemplate = tmpl ? tmpl : new Template();
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getTemplates(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.templateService.getTemplates(this.loggedAccount.email)
            .subscribe( { 
                next: (data: ReturnObject<ApiTemplate[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.templates = Converters.ApiTemplateToTemplate(data.value);
                    var lastSavedInx = this.templates.findIndex( x => x.name.toLowerCase() == 'last-saved');
                    if ( !this.selectedTemplate ) {
                        this.selectedTemplate = lastSavedInx != -1 
                            ? this.templates[lastSavedInx] 
                            : this.templates.length > 0
                                ? this.templates[0]
                                : this.selectedTemplate;
                    } else {
                        const tmpl: Template | undefined =  this.templates.find( x => x.name === this.selectedTemplate.name);
                        this.selectedTemplate = tmpl ? tmpl : new Template();
                    }
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    getTemplate(): void {
        this.msgService.ontoggleSpinnerSet(true);
        this.templateService.getTemplate(this.loggedAccount.email, this.selectedTemplate.name, this.selectedWeek.weekNo)
            .subscribe( { 
                next: (data: ReturnObject<ApiTemplate[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.selectedTemplate = Converters.ApiTemplateToTemplate(data.value)[0];
                    this.setDirty(true);
                    this.applyTemplate();
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    saveTheTemplate() {
        this.msgService.ontoggleSpinnerSet(true);
        this.templateService
            .saveTemplate(this.loggedAccount.email, this.templateName, this.selectedWeek.timeCard.timecardRows)
            .subscribe( { 
                next: (data: ReturnObject<ApiTemplate[]>) => {
                    if (data.isFailure) {
                        this.statusMessagesService.showToasterError('Error', data.failureMessage);
                        return;
                    }
                    this.templates = Converters.ApiTemplateToTemplate(data.value);
                    const tmpl: Template | undefined = this.templates.find(x => x.name = this.templateName);
                    this.selectedTemplate = tmpl ? tmpl : new Template();
                    this.templateName = '';
                    this.statusMessagesService.showToasterSuccess('Saved', 'Template was saved');
                },
                error: (err) => {         
                    this.statusMessagesService.showHttpError(err);
                },
                complete: () => {         
                    this.msgService.ontoggleSpinnerSet(false);
                }
            }
        );
    }

    deleteTemplate() {
        this.msgService.ontoggleSpinnerSet(true);
        if (this.selectedDeleteTemplate != null) {
            this.templateService
                .deleteTemplate(this.loggedAccount.email, this.selectedDeleteTemplate.name)
                .subscribe( { 
                    next: (data: ReturnObject<ApiTemplate[]>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }
                        this.selectedDeleteTemplate = null
                        this.templates = Converters.ApiTemplateToTemplate(data.value);
                        this.selectedTemplate = this.templates.length > 0  ? this.templates[0] : new Template();;
                        this.templateName = '';
                        this.statusMessagesService.showToasterSuccess('Deleted', 'Template was deleted');
                    },
                    error: (err) => {         
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {         
                        this.msgService.ontoggleSpinnerSet(false);
                    }
                }
            );
        }
    }

    setSelectedTask(timecardRow: TimecardRow, task: Task) {
        timecardRow.task = task;
        this.setDirty(true);
    }
     
    getWeekOptionText(week: Week): string {
        return `${week.status.toLowerCase() != '' ? 'saved' : ''}`;
    }
    
    getDateFromWeekDay(weekDay: number): Date {
        if (!this.selectedWeek) {
            return new Date();
        }

        const date = new Date(this.selectedWeek.firstDayOfWeek);
        date.setDate(date.getDate() + weekDay);
        return date;
    }

    removeInvalidRows(): void {
        this.selectedWeek.timeCard.timecardRows = this.selectedWeek
            .timeCard
            .timecardRows
            .filter(x => x.project && x.task);
        this.deletedRows = this.deletedRows.filter(x => x.project && x.task);
    }

    addRow(): void {
        this.projectCtrl.reset();
        this.setDirty(true);
        this.selectedWeek.timeCard.timecardRows.push(new TimecardRow());
    }

    removeRow(index: number): void {
        this.deletedRows.push(this.selectedWeek.timeCard.timecardRows[index]);
        this.selectedWeek.timeCard.timecardRows.splice(index, 1);
        this.setDirty(true);
    }

    setDirty(value: boolean): void {
        this.isDirty = value;
    }

    removeAllRows(): void {
        this.selectedWeek.timeCard.timecardRows.forEach( (row) => this.deletedRows.push(row));
        this.selectedWeek.timeCard.timecardRows = [];
        this.setDirty(true);
    }

    applyTemplate(): void {
        if (this.selectedTemplate != null) {
            const tempTimeCardRows = new Array<TimecardRow>();

            for (const timeCardRow of this.selectedTemplate.timeCardRows) {
                const matchingRows = this.selectedWeek.timeCard.timecardRows
                    .filter(x => x.project.projNo == timeCardRow.project.projNo && x.task.taskNo == timeCardRow.task.taskNo);

                if (matchingRows.length == 0) {
                    tempTimeCardRows.push(timeCardRow);
            }
        }
            this.selectedWeek.timeCard.timecardRows = this.selectedWeek.timeCard.timecardRows.concat(tempTimeCardRows);
            this.statusMessagesService.showToasterInfo('Applied', 'Template was applied');
        }
    }

    calcMinusHours(selectedWeek: Week): boolean {
        let hasMinusHours = false;

        hasMinusHours = selectedWeek.timeCard.timecardRows.some(item =>
            Number(item.mon) < 0 ||
            Number(item.tue) < 0 ||
            Number(item.wed) < 0 ||
            Number(item.thu) < 0 ||
            Number(item.fri) < 0 ||
            Number(item.sat) < 0 ||
            Number(item.sun) < 0
        );

        return hasMinusHours;
    }

    calcTotalHours(selectedWeek: Week): number {
        let totHours = 0;
        
        selectedWeek.timeCard.timecardRows.forEach(item => {
            totHours += (Number(item.mon) 
                + Number(item.tue) 
                + Number(item.wed) 
                + Number(item.thu) 
                + Number(item.fri) 
                + Number(item.sat) 
                + Number(item.sun));
        });

        return totHours;
    }

    createEmptyTimeCard(): void {
        if (this.weeks.length > 0) {
            this.weeks[0].timeCard = new TimeCard(null);
            this.weeks[0].timeCard.timecardRows = [];
        }
    }

    filterWeeks(): void {
        this.weeks = this.weeks.filter(x => 
            !(x.status.toLowerCase().trim() == 'approved' || x.status.toLowerCase().trim() === 'submitted') );
    }

    sortWeeks(): void {
        this.weeks = this.weeks.sort(function(a, b) {
            return a.weekNo == b.weekNo 
                ? 0 
                : +(a.weekNo >= b.weekNo) || -1;
        });
    }

    setListWeekString() : void{
        this.weeks.forEach(item => {
            const first = item.firstDayOfWeek.split('T')[0];
            const last = item.lastDayOfWeek.split('T')[0];
            item.displayWeek = `Week: ${item.weekNo.slice(4, 6)}: ${first} to ${last}`;
        });
    }

    save( type: string ): void {
        const isEmptyTask: boolean = this.selectedWeek.timeCard.timecardRows.find(x => x.task == null || x.task.taskNo.trim() == '') != null;
        const isEmptyProj: boolean = this.selectedWeek.timeCard.timecardRows.find(x => x.project == null || x.project.projNo.trim() == '') != null;
        if (isEmptyProj || isEmptyTask) {
            this.statusMessagesService.showToasterInfo('Missing proj or task', 'Time Card has rows where project and/or task is not proper. Tip - dont past codes.');
            return;
        }

        this.removeInvalidRows();
        if (this.anyDuplicates()) {
            this.statusMessagesService.showToasterInfo('Duplicate', 'Time Card contains duplicates. You can only have one row with the same project and task.');
            return;
        }

        const hasMinusHours = this.calcMinusHours(this.selectedWeek)
        if (hasMinusHours) {
            this.statusMessagesService.showToasterInfo('Minus hours not allowed ', 'Time Card was not saved/submitted: Minus hours are not allowed.');
            return;
        }
        const totalHours = this.calcTotalHours(this.selectedWeek);
        if ( totalHours < 0 || totalHours > 999) {
            this.statusMessagesService.showToasterInfo('Too many hours ', 'Time Card was not saved/submitted: Total hours count is too large/small. Min hours is 0 and max hours is 999.');
            return;
        }        
        if (type == "save" )        
            this.saveTimeCard()
        else
            this.submitTimeCard();
    }


    // ----------------------------------------------------------------------------------
    // -- LOGIC
    // ----------------------------------------------------------------------------------
    setSelectedTemplate(template: Template): void {
        this.selectedTemplate = template;
    }


    isTimeCardEditable(): boolean {
        try {
            return this.selectedWeek.timeCard.status.toLowerCase() === 'saved' 
            || this.selectedWeek.timeCard.status.toLowerCase() === '';
        } catch (error) {
            return true;
        }
    }

    isRowDiabled(weekDay: number, row: TimecardRow): boolean {
        if (!row.task  || row.invalidProj || row.invalidTask) {
            return true;
        }

        if (this.selectedWeek != null &&
            (this.selectedWeek.timeCard.status.toLowerCase() == 'submitted' ||
            this.selectedWeek.timeCard.status.toLowerCase() == 'approved')) {
            return true;
        }

        const date = this.getDateFromWeekDay(weekDay);
        if (row.task.startDt != null && date < row.task.startDt) {
            return true;
        }

        if (row.task.endDt != null && date > row.task.endDt) {
            return true;
        }

        return false;
    }

    isTemplateSelected(): boolean {
        return this.selectedTemplate == null || this.selectedTemplate.name == '';
    }

    anyDuplicates(): boolean {
        for (const timeCardRow of this.selectedWeek.timeCard.timecardRows) {
            const matchingRows = this.selectedWeek
                .timeCard
                .timecardRows
                .filter(x => x.project.projNo == timeCardRow.project.projNo && x.task.taskNo == timeCardRow.task.taskNo);

            if (matchingRows.length > 1) {
                return true;
            }
        }
        return false;
    }

    isSaveDisabled(): boolean {
        const isEmptyTask: boolean = this.selectedWeek.timeCard.timecardRows.find(x => x.task == null || x.task.taskNo.trim() == '') != null;
        const isEmptyProj: boolean = this.selectedWeek.timeCard.timecardRows.find(x => x.project == null || x.project.projNo.trim() == '') != null;
        try {
            return this.selectedWeek === null
                || this.selectedWeek.status.toLowerCase() === 'approved'
                || this.selectedWeek.status.toLowerCase() === 'submitted'
                || isEmptyProj
                || isEmptyTask;

        } catch (error) {
            return true;
        }
    }

    isDeleteDisabled(): boolean {
        return this.selectedWeek === undefined
            || this.selectedWeek === null
            || this.selectedWeek.status.toLowerCase() === 'approved'
            || this.selectedWeek.status.toLowerCase() === '';
    }


    // --------------------------------------------------------------
    // template dialog
    // --------------------------------------------------------------

    setSelectedDeleteTemplate(template: Template): void {
        this.selectedDeleteTemplate = template;
    }

    getListTemplates() {
        return this.templates.filter( x => x.name !== 'Last-Saved');
    }

    isDisabledSaveTemplate() {
         return  this.selectedWeek === null
                 || this.selectedWeek.timeCard.timecardRows.length === 0;
    }


    isDeleteTemplateDisabled(): boolean {
        return this.selectedDeleteTemplate == null;
    }

    setAlreadyExist(): void {
        this.templateAlreadyExists = this.templates.filter(x => x.name.toUpperCase() == this.templateName.toUpperCase()).length > 0;
    }
    saveTemplate() {
        this.templateAlreadyExists = false;
        this.removeInvalidTemplateRows();
        const templNameExists = this.templates.filter(x => x.name.toUpperCase() == this.templateName.toUpperCase()).length > 0;
        if (templNameExists) {
            this.templateAlreadyExists = true;
        } else {
            this.saveTheTemplate();
        }
    }

    removeInvalidTemplateRows() {
        this.selectedWeek.timeCard.timecardRows = this.selectedWeek.timeCard.timecardRows.filter(x => x.project && x.task);
        this.deletedTemplateRows = this.deletedTemplateRows.filter(x => x.project && x.task);
    }
}
